import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Container } from "@/components/ui-blocks";
import { useCart } from "@/context/CartContext";
import { createOrder } from "@/services/api";
import { createPaymentRequest } from "@/services/stripe";
import { usePricing } from "@/hooks/usePricing";
import { COUNTRIES } from "@/data/countries";
import { leadTimeSentence } from "@/lib/deliveryCopy";
import QuantityInput from "@/components/QuantityInput";
import BulkNudge from "@/components/BulkNudge";
import { Loader2, ShieldCheck, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";


export default function Checkout() {
  const { items, subtotal, updateQty } = useCart();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "Singapore",
    postal: "",
    country: "Singapore",
    notes: "",
  });
  const [paymentState, setPaymentState] = useState("idle"); // idle | redirecting | pending | success | failed

  // Priced against the chosen country, so the zone rate, free-delivery rule and
  // quoted delivery time all follow the address.
  const { settings, quote } = usePricing(items, form.country);
  const leadTime = leadTimeSentence(settings, quote.zoneId);

  if (items.length === 0 && paymentState === "idle") {
    return <Navigate to="/cart" replace />;
  }

  const shipping = quote.shipping;
  const total = quote.total;
  // A zone the client has not priced cannot be checked out online — they take
  // those orders by email instead.
  const enquiryOnly = quote.requiresEnquiry;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Phone is deliberately optional — buyers who prefer to stay anonymous
  // should not be turned away. Email is the required contact channel.
  const requiredFilled =
    form.fullName && form.email && form.address1 && form.postal;

  const goNext = () => {
    if (!requiredFilled) {
      toast.error("Please complete all required fields");
      return;
    }
    setStep(2);
  };

  const placeOrder = async () => {
    setPaymentState("redirecting");
    try {
      // 1) Create the order (status: pending) on the backend.
      const order = await createOrder({
        customerName: form.fullName,
        email: form.email,
        phone: form.phone,
        address: form,
        products: items.map((i) => ({
          title: i.title,
          qty: i.qty,
          price: i.price,
          productId: i.id,
        })),
        total,
      });
      if (!order || !order.orderId) throw new Error("Could not create your order");

      // 2) Create a Stripe Checkout Session (server-side) and redirect the
      //    customer to Stripe's secure hosted payment page. Stripe's webhook
      //    confirms payment and updates the order server-side.
      const res = await createPaymentRequest({
        orderId: order.orderId,
        redirectUrl: `${window.location.origin}/order-confirmation/${order.orderId}`,
      });

      if (!res.success) {
        setPaymentState("failed");
        toast.error("Payment failed", { description: res.error });
        return;
      }

      window.location.href = res.paymentUrl;
    } catch (e) {
      setPaymentState("failed");
      toast.error("Something went wrong", { description: e.message });
    }
  };

  if (paymentState !== "idle") {
    return <PaymentStateScreen state={paymentState} onRetry={() => setPaymentState("idle")} />;
  }

  return (
    <div data-testid="checkout-page" className="bg-[#FDFBF7]">
      <Container>
        <div className="py-12 md:py-16">
          <div className="overline text-[#946f36]">Secure Checkout</div>
          <h1 className="mt-3 font-serif-display text-5xl font-light">Checkout</h1>
          <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
            <Stepper step={1} active={step >= 1} label="Information" />
            <span className="w-10 h-px bg-[#6e5022]/30" />
            <Stepper step={2} active={step >= 2} label="Review & Pay" />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 pb-20">
          <div className="lg:col-span-8">
            {step === 1 && (
              <form
                data-testid="checkout-info-form"
                className="space-y-8"
                onSubmit={(e) => { e.preventDefault(); goNext(); }}
              >
                <Section title="Contact information">
                  <Field label="Full name *" testId="checkout-fullName" value={form.fullName} onChange={(v) => update("fullName", v)} />
                  <Field label="Email *" type="email" testId="checkout-email" value={form.email} onChange={(v) => update("email", v)} />
                  <Field label="Phone (optional)" testId="checkout-phone" value={form.phone} onChange={(v) => update("phone", v)} />
                  <p className="text-xs text-[#946f36] -mt-3">
                    Optional — we&apos;ll only use it if there&apos;s a problem with your delivery.
                  </p>
                </Section>

                <Section title="Delivery address">
                  <Field label="Address line 1 *" testId="checkout-address1" value={form.address1} onChange={(v) => update("address1", v)} />
                  <Field label="Address line 2" testId="checkout-address2" value={form.address2} onChange={(v) => update("address2", v)} />
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="City" testId="checkout-city" value={form.city} onChange={(v) => update("city", v)} />
                    <Field label="Postal code *" testId="checkout-postal" value={form.postal} onChange={(v) => update("postal", v)} />
                  </div>
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.15em] text-[#4A3728]">Country *</span>
                    <select
                      data-testid="checkout-country"
                      value={form.country}
                      onChange={(e) => update("country", e.target.value)}
                      className="w-full bg-transparent border-b border-[#6e5022]/40 focus:border-[#6e5022] py-2.5 outline-none text-sm"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </label>
                  <Field label="Order notes" testId="checkout-notes" value={form.notes} onChange={(v) => update("notes", v)} />
                </Section>

                {/* Stopped here rather than at the payment step: there is no
                    point filling in a delivery address for a destination we
                    cannot ship to online. The enquiry route is in the summary
                    alongside. */}
                <button
                  type="submit"
                  disabled={enquiryOnly}
                  data-testid="checkout-continue-button"
                  title={enquiryOnly ? `We arrange delivery to ${form.country} by enquiry` : undefined}
                  className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#6e5022]"
                >
                  Continue to Payment <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </form>
            )}

            {step === 2 && (
              <div data-testid="checkout-review-section" className="space-y-8">
                <ReviewCard title="Contact" testId="review-contact">
                  <div>{form.fullName}</div>
                  <div className="text-sm text-[#4A3728]">{form.email}</div>
                  {form.phone && (
                    <div className="text-sm text-[#4A3728]">{form.phone}</div>
                  )}
                </ReviewCard>
                <ReviewCard title="Shipping to" testId="review-shipping">
                  <div className="text-sm text-[#4A3728]">{form.address1}{form.address2 ? `, ${form.address2}` : ""}</div>
                  <div className="text-sm text-[#4A3728]">{form.city}, {form.postal}, {form.country}</div>
                </ReviewCard>

                <div className="border border-[#6e5022]/25 p-6 bg-[#F4F1EA]">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-[#6e5022]" strokeWidth={1.5} />
                    <div className="font-serif-display text-2xl">Secure card payment</div>
                  </div>
                  <p className="mt-3 text-sm text-[#4A3728]">
                    You will be redirected to Stripe&apos;s secure payment page to
                    complete your purchase. All major credit and debit cards are
                    supported.
                  </p>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    onClick={() => setStep(1)}
                    data-testid="checkout-back-button"
                    className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={placeOrder}
                    disabled={enquiryOnly}
                    data-testid="checkout-pay-button"
                    title={enquiryOnly ? `We arrange delivery to ${form.country} by email` : undefined}
                    className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#6e5022]"
                  >
                    Proceed to Payment
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <aside className="lg:col-span-4">
            <div className="bg-[#F4F1EA] border border-[#6e5022]/20 p-7 sticky top-32">
              <div className="overline text-[#946f36] mb-4">Order Summary</div>
              <div className="space-y-3 max-h-60 overflow-auto pr-2">
                {/* Editable here too: reaching checkout and finding the
                    quantity frozen forces a trip back to the cart, which is
                    exactly where someone decides to take up a bulk offer. */}
                {items.map((i) => (
                  <div key={i.id} className="flex gap-3" data-testid={`checkout-summary-item-${i.id}`}>
                    {i.image ? (
                      <img src={i.image} alt={i.title} className="w-12 h-14 object-contain bg-[#FDFBF7]" />
                    ) : (
                      <div className="w-12 h-14 bg-[#FDFBF7]" aria-hidden="true" />
                    )}
                    <div className="flex-1 text-sm">
                      <div className="font-serif-display leading-tight">{i.title}</div>
                      <div className="mt-1.5">
                        <QuantityInput
                          value={i.qty}
                          onChange={(n) => updateQty(i.id, n)}
                          size="sm"
                          testId={`checkout-qty-${i.id}`}
                          label={`Quantity of ${i.title}`}
                        />
                      </div>
                    </div>
                    <div className="text-sm whitespace-nowrap">SGD {(i.price * i.qty).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#6e5022]/30 mt-5 pt-4 space-y-1.5 text-sm">
                <Row label="Subtotal" value={`SGD ${subtotal.toFixed(2)}`} testId="checkout-subtotal" />
                {quote.discountLines.map((d) => (
                  <Row
                    key={d.ruleId || d.label}
                    label={d.label}
                    value={`− SGD ${d.amount.toFixed(2)}`}
                    testId={`checkout-discount-${d.ruleId || "line"}`}
                    tone="saving"
                  />
                ))}
                <Row
                  label={quote.zoneLabel ? `Delivery · ${quote.zoneLabel}` : "Delivery"}
                  value={
                    enquiryOnly
                      ? "By enquiry"
                      : quote.freeDelivery
                        ? "Free"
                        : `SGD ${shipping.toFixed(2)}`
                  }
                  testId="checkout-shipping"
                  tone={quote.freeDelivery ? "saving" : undefined}
                />
                {quote.freeDelivery && quote.freeDeliveryLabel && (
                  <div className="text-[11px] text-green-800 pt-0.5" data-testid="checkout-free-delivery-label">
                    {quote.freeDeliveryLabel}
                  </div>
                )}
                {!enquiryOnly && leadTime && (
                  <div className="text-[11px] text-[#946f36] pt-1 leading-relaxed" data-testid="checkout-lead-time">
                    {leadTime}
                  </div>
                )}
              </div>
              <div className="border-t border-[#6e5022]/30 mt-3 pt-4 flex items-baseline justify-between">
                <span className="overline">Total</span>
                <span data-testid="checkout-total" className="font-serif-display text-2xl">SGD {total.toFixed(2)}</span>
              </div>

              <BulkNudge quote={quote} testId="checkout-nudge" className="mt-5" />

              {enquiryOnly && (
                <div
                  data-testid="checkout-enquiry-notice"
                  className="mt-5 border border-[#946f36]/50 bg-[#FDFBF7] p-4 text-xs leading-relaxed text-[#4A3728]"
                >
                  <p>
                    We don&apos;t yet ship to <strong>{form.country}</strong> online.
                    Write to our delivery team with your order and we&apos;ll arrange it
                    for you.
                  </p>
                  <Link
                    to={`/contact?subject=${encodeURIComponent(
                      `Delivery enquiry — ${form.country}`,
                    )}&message=${encodeURIComponent(
                      `I would like to order ${items.reduce((n, i) => n + i.qty, 0)} item(s) ` +
                        `for delivery to ${form.country}.\n\nDelivery address:\n`,
                    )}`}
                    data-testid="checkout-enquire-button"
                    className="mt-3 inline-flex items-center gap-2 border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-5 py-2.5 uppercase tracking-[0.18em] transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Enquire Now
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </Container>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="overline text-[#946f36] mb-5">{title}</div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-[#4A3728]">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#6e5022]/40 focus:border-[#6e5022] py-2.5 outline-none text-sm"
      />
    </label>
  );
}

function ReviewCard({ title, children, testId }) {
  return (
    <div data-testid={testId} className="border border-[#6e5022]/25 p-6">
      <div className="overline text-[#946f36] mb-2">{title}</div>
      <div className="font-serif-display text-xl text-[#281a12]">{children}</div>
    </div>
  );
}

function Row({ label, value, testId, tone }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-[#4A3728]">{label}</span>
      <span
        data-testid={testId}
        className={`text-right whitespace-nowrap ${tone === "saving" ? "text-green-800" : ""}`}
      >{value}</span>
    </div>
  );
}

function Stepper({ step, active, label }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "text-[#6e5022]" : "text-[#946f36]/60"}`}>
      <span className={`w-6 h-6 border flex items-center justify-center text-[10px] ${active ? "border-[#6e5022]" : "border-[#946f36]/40"}`}>
        {step}
      </span>
      {label}
    </div>
  );
}

function PaymentStateScreen({ state, onRetry }) {
  const states = {
    redirecting: { title: "Redirecting to secure payment…", body: "Hang tight while we securely hand off your payment.", icon: <Loader2 className="w-10 h-10 animate-spin text-[#6e5022]" strokeWidth={1.5} /> },
    pending: { title: "Awaiting confirmation", body: "We are confirming your payment.", icon: <Loader2 className="w-10 h-10 animate-spin text-[#6e5022]" strokeWidth={1.5} /> },
    success: { title: "Payment successful", body: "Your order has been received. Redirecting to confirmation…", icon: <ShieldCheck className="w-10 h-10 text-green-700" strokeWidth={1.5} /> },
    failed: { title: "Payment failed", body: "We couldn't process your payment. Please try again.", icon: <ShieldCheck className="w-10 h-10 text-red-700" strokeWidth={1.5} /> },
  };
  const s = states[state];

  return (
    <div data-testid={`payment-state-${state}`} className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="flex justify-center">{s.icon}</div>
        <h2 className="mt-6 font-serif-display text-3xl">{s.title}</h2>
        <p className="mt-3 text-[#4A3728]">{s.body}</p>
        {state === "failed" && (
          <button
            data-testid="payment-retry-button"
            onClick={onRetry}
            className="mt-8 bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Try Again
          </button>
        )}
        {state === "pending" && (
          <Link to="/track" data-testid="payment-track-link" className="mt-8 inline-block border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
            Track Order
          </Link>
        )}
      </div>
    </div>
  );
}
